import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	Dialog,
	ManageEmailsIcon,
	ModuleHeader,
	SpinnerWithBackground,
	TagField,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import type { EmailTemplate, EmailTemplateSearchInput } from './types'

export const EmailTemplatesPage = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [selectedItems, setSelectedItems] = useState<EmailTemplate[]>([])
	const navigate = useNavigate()
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const toast = useToast()
	const queryClient = useQueryClient()

	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const deleteEmailTemplatesMutation = useMutation({
		...trpc.admin.emailTemplates.delete.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Email templates deleted')
		},
		onError: (error) => {
			toast.showToast(`Error deleting email template(s): ${error.message}`, 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.emailTemplates.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleRowClick = (emailTemplate: EmailTemplate) => {
		navigate(`/organizations/${organizationId}/email-templates/${emailTemplate.id}`)
	}

	const handleDeleteEmailTemplates = () => {
		deleteEmailTemplatesMutation.mutate({
			organizationId: organizationId as string,
			ids: selectedItems.map((x) => x.id),
		})
	}

	if (isLoadingOrg) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	const columns: Column<EmailTemplate>[] = [
		{
			field: 'name',
			label: 'Template name',
			sortable: true,
			render: (emailTemplate: EmailTemplate) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{emailTemplate.name}
				</Text>
			),
		},
		{
			field: 'subject',
			label: 'Subject',
			sortable: true,
			render: (emailTemplate: EmailTemplate) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{emailTemplate.subject}
				</Text>
			),
		},
		{
			field: 'tags',
			label: 'Tags',
			sortable: false,
			render: ({ tags }: EmailTemplate) => {
				if (!tags?.length) return

				return <TagField value={tags ? tags.map((x) => ({ id: x, label: x })) : []} readonly={true} />
			},
		},
	]

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: organization?.name || '', to: `/organizations/${organizationId}` },
					{ label: 'Email Templates' },
				]}
				title='Email Templates'
				logo={{
					url: organization?.logo || '',
					name: organization?.name || '',
				}}
			/>
			<div className='mt-6'>
				<GenericTable<EmailTemplate, EmailTemplateSearchInput>
					columns={columns}
					idField='id'
					selectable
					onSelectionChange={(items) => setSelectedItems(items)}
					queryOptionsGenerator={(options) => ({
						...trpc.admin.emailTemplates.search.queryOptions({
							...options,
							organizationId: organizationId as string,
						}),
					})}
					onRowClick={handleRowClick}
					showSearch
					showPagination
					actionButtons={[
						{
							children: 'Create email template',
							icon: AddIcon,
							iconPosition: 'left',
							onClick: () => navigate(`/organizations/${organizationId}/email-templates/create`),
							variant: 'primary',
						},
					]}
					emptyState={{
						icon: ManageEmailsIcon,
						title: 'No email templates created yet',
						description: 'Get started by creating a new email template',
						actionLabel: 'Create email template',
						onAction: () => navigate(`/organizations/${organizationId}/email-templates/create`),
					}}
				/>
			</div>

			<Dialog
				title='Delete email template(s)'
				message={`You are about to delete ${selectedItems.length} email template(s) from this organization. This action cannot be reversed.`}
				type='archive'
				confirmLabel='Archive'
				requireConfirmText
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				onConfirm={handleDeleteEmailTemplates}
			/>

			{selectedItems.length > 0 && (
				<ActionBar
					rightActions={[
						{
							label: 'Delete',
							variant: 'destructive',
							onClick: () => setIsDeleteModalOpen(true),
						},
					]}
				/>
			)}
		</main>
	)
}
